import { COLORS } from "@game/config";

const BULLET_GRADIENT_TEMPLATE_LENGTH = 100;

const colorRGBCache = new Map<string, string>();

/**
 * Module-level gradient cache keyed by hex color.
 * Each gradient is a template from (0,0) to (100,0) that gets
 * transformed via canvas context to match each bullet trail's orientation.
 */
const bulletGradientCache = new Map<string, CanvasGradient>();
let bulletGradientContext: CanvasRenderingContext2D | null = null;

function hexToRGB(hex: string): string {
  const cached = colorRGBCache.get(hex);
  if (cached) return cached;

  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const rgb = `${r}, ${g}, ${b}`;
  colorRGBCache.set(hex, rgb);
  return rgb;
}

function getBulletGradient(
  ctx: CanvasRenderingContext2D,
  colorHex: string,
): CanvasGradient {
  if (bulletGradientContext !== ctx) {
    bulletGradientCache.clear();
    bulletGradientContext = ctx;
  }

  const cachedGradient = bulletGradientCache.get(colorHex);
  if (cachedGradient) return cachedGradient;

  const colorRGB = hexToRGB(colorHex);
  const len = BULLET_GRADIENT_TEMPLATE_LENGTH;

  const gradient = ctx.createLinearGradient(0, 0, len, 0);
  gradient.addColorStop(0, `rgba(${colorRGB}, 0)`);
  gradient.addColorStop(0.5, `rgba(${colorRGB}, 0.3)`);
  gradient.addColorStop(1, `rgba(${colorRGB}, 0.8)`);

  bulletGradientCache.set(colorHex, gradient);
  return gradient;
}

export function renderBulletsWithTrails(
  ctx: CanvasRenderingContext2D,
  state: any,
  isVisible: (x: number, y: number, margin?: number) => boolean,
  getWrappedPosition: (x: number, y: number) => { x: number; y: number },
) {
  for (const [, interpolation] of state.bulletsInterpolation) {
    if (interpolation.markedForRemoval) continue;
    const bulletX = interpolation.currentPos.x;
    const bulletY = interpolation.currentPos.y;

    if (isVisible(bulletX, bulletY)) {
      const currentTime = Date.now();
      const bulletRadius = 5 * (interpolation.shooterScale || 1.0);
      const isMyBullet = interpolation.ownerId === state.playerId;

      const owner = state.players.get(interpolation.ownerId);
      const bulletColor =
        owner?.color || (isMyBullet ? COLORS.PLAYER : COLORS.ENEMY);

      if (interpolation.trail && interpolation.trail.length > 1) {
        const wrappedBullet = getWrappedPosition(bulletX, bulletY);
        const oldestTrail = interpolation.trail[0];
        if (!oldestTrail) continue;
        const wrappedOldest = getWrappedPosition(
          oldestTrail.pos.x,
          oldestTrail.pos.y,
        );

        const deltaX = wrappedBullet.x - wrappedOldest.x;
        const deltaY = wrappedBullet.y - wrappedOldest.y;
        const trailLength = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (trailLength < 1) continue;

        const trailAngle = Math.atan2(deltaY, deltaX);
        const scaleFactor = trailLength / BULLET_GRADIENT_TEMPLATE_LENGTH;
        const gradient = getBulletGradient(ctx, bulletColor);

        ctx.save();
        ctx.translate(wrappedOldest.x, wrappedOldest.y);
        ctx.rotate(trailAngle);
        ctx.scale(scaleFactor, 1);

        ctx.strokeStyle = gradient;
        // Don't divide by scaleFactor - let width scale naturally with speed
        // Slow speed = thin trail, fast speed = normal trail
        ctx.lineWidth = bulletRadius * 1.5;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        const cosAngle = Math.cos(-trailAngle);
        const sinAngle = Math.sin(-trailAngle);
        const invScale = 1 / scaleFactor;

        ctx.beginPath();

        const bulletRelX = wrappedBullet.x - wrappedOldest.x;
        const bulletRelY = wrappedBullet.y - wrappedOldest.y;
        const bulletLocalX =
          (bulletRelX * cosAngle - bulletRelY * sinAngle) * invScale;
        const bulletLocalY = bulletRelX * sinAngle + bulletRelY * cosAngle;
        ctx.moveTo(bulletLocalX, bulletLocalY);

        for (let i = interpolation.trail.length - 1; i >= 0; i--) {
          const trailPoint = interpolation.trail[i];
          if (!trailPoint) continue;
          const trailAge = currentTime - trailPoint.timestamp;
          if (trailAge < 100) {
            const wrappedPoint = getWrappedPosition(
              trailPoint.pos.x,
              trailPoint.pos.y,
            );
            const relX = wrappedPoint.x - wrappedOldest.x;
            const relY = wrappedPoint.y - wrappedOldest.y;
            const localX = (relX * cosAngle - relY * sinAngle) * invScale;
            const localY = relX * sinAngle + relY * cosAngle;
            ctx.lineTo(localX, localY);
          }
        }
        ctx.stroke();
        ctx.restore();
      }

      const fadeStartTime = 0.5;
      const fadeDuration = 3.5;
      let alpha = 1.0;
      if (interpolation.age >= fadeStartTime) {
        const fadeProgress = (interpolation.age - fadeStartTime) / fadeDuration;
        alpha = Math.max(0, 1 - fadeProgress);
      }
      ctx.fillStyle = bulletColor;
      ctx.globalAlpha = alpha;
      const wrapped = getWrappedPosition(bulletX, bulletY);
      ctx.beginPath();
      ctx.arc(wrapped.x, wrapped.y, bulletRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
    }
  }
}
